import React, { useState, useMemo } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  TreeSelect,
  Switch,
  message,
  Space,
  Tag,
  Popconfirm,
  Typography,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useBranchContext } from "../../contexts/branch-context";
import { currencySymbol } from "../../utils/currency";
import {
  SettingItem,
  SettingRow,
  SettingContent,
  SettingLabel,
  SettingDescription,
  StyledSwitch
} from "../../styles/shared-components";
import {
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getCouponById,
  getProductCategories,
} from "../../service";

const { Text } = Typography;
const { Option } = Select;

export const Coupons: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const curSymbol = currencySymbol(currentBranch?.currency);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<any>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // Fetch all coupons
  const { data: coupons, isLoading } = useQuery("coupons", getCoupons);

  // Fetch product categories with products for TreeSelect
  const { data: categories } = useQuery("product-categories-for-coupons", () =>
    getProductCategories()
  );

  // Transform categories into TreeSelect data structure
  const treeData = useMemo(() => {
    if (!categories) return [];

    return categories.map((category: any) => ({
      title: category.name,
      value: `category-${category.id}`,
      key: `category-${category.id}`,
      selectable: false, // Categories are not selectable, only products
      children: (category.productBases || []).map((product: any) => ({
        title: product.name,
        value: product.id,
        key: `product-${product.id}`,
      })),
    }));
  }, [categories]);

  // Create coupon mutation
  const createMutation = useMutation(createCoupon, {
    onSuccess: () => {
      message.success(t("couponsPage.couponCreatedSuccess"));
      queryClient.invalidateQueries("coupons");
      handleCloseModal();
    },
    onError: (error: any) => {
      message.error(
        t("couponsPage.couponCreateError", { error: error?.response?.data?.message || error.message })
      );
    },
  });

  // Update coupon mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => updateCoupon(id, data),
    {
      onSuccess: () => {
        message.success(t("couponsPage.couponUpdatedSuccess"));
        queryClient.invalidateQueries("coupons");
        handleCloseModal();
      },
      onError: (error: any) => {
        message.error(
          t("couponsPage.couponUpdateError", { error: error?.response?.data?.message || error.message })
        );
      },
    }
  );

  // Delete coupon mutation
  const deleteMutation = useMutation(deleteCoupon, {
    onSuccess: () => {
      message.success(t("couponsPage.couponDeletedSuccess"));
      queryClient.invalidateQueries("coupons");
    },
    onError: (error: any) => {
      message.error(
        t("couponsPage.couponDeleteError", { error: error?.response?.data?.message || error.message })
      );
    },
  });

  const handleOpenModal = (coupon?: any) => {
    if (coupon) {
      setEditingCoupon(coupon);
      form.setFieldsValue({
        code: coupon.code,
        type: coupon.type,
        value: coupon.value,
        scope: coupon.scope,
        minimumOrderValue: coupon.minimumOrderValue / 100,
        maxUsageCount: coupon.maxUsageCount,
        isActive: coupon.isActive,
        productIds: coupon.products?.map((p: any) => p.id) || [],
      });
    } else {
      setEditingCoupon(null);
      form.resetFields();
      form.setFieldsValue({
        isActive: true,
        type: "PERCENTAGE",
        scope: "CART",
        minimumOrderValue: 0,
        maxUsageCount: 1,
      });
    }
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setEditingCoupon(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const couponData = {
        code: values.code.toUpperCase(),
        type: values.type,
        value: values.value,
        scope: values.scope,
        minimumOrderValue: Math.round(values.minimumOrderValue * 100),
        maxUsageCount: values.maxUsageCount,
        isActive: values.isActive ?? true,
        productIds:
          values.scope === "SPECIFIC_PRODUCTS" ? values.productIds : undefined,
      };

      if (editingCoupon) {
        updateMutation.mutate({ id: editingCoupon.id, data: couponData });
      } else {
        createMutation.mutate(couponData);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const columns = [
    {
      title: t("couponsPage.columnCode"),
      dataIndex: "code",
      key: "code",
      render: (code: string) => (
        <Text strong style={{ fontFamily: "monospace" }}>
          {code}
        </Text>
      ),
    },
    {
      title: t("couponsPage.columnType"),
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag color={type === "PERCENTAGE" ? "blue" : "green"}>
          {type === "PERCENTAGE" ? t("couponsPage.typePercentage") : t("couponsPage.typeFixed")}
        </Tag>
      ),
    },
    {
      title: t("couponsPage.columnValue"),
      dataIndex: "value",
      key: "value",
      render: (value: number, record: any) =>
        record.type === "PERCENTAGE"
          ? `${value}%`
          : `${(value / 100).toFixed(2)} ${curSymbol}`,
    },
    {
      title: t("couponsPage.columnScope"),
      dataIndex: "scope",
      key: "scope",
      render: (scope: string) => (
        <Tag color={scope === "CART" ? "purple" : "orange"}>
          {scope === "CART" ? t("couponsPage.scopeCart") : t("couponsPage.scopeProducts")}
        </Tag>
      ),
    },
    {
      title: t("couponsPage.columnMinOrder"),
      dataIndex: "minimumOrderValue",
      key: "minimumOrderValue",
      render: (value: number) =>
        value > 0 ? `${(value / 100).toFixed(2)} ${curSymbol}` : t("couponsPage.none"),
    },
    {
      title: t("couponsPage.columnUsage"),
      key: "usage",
      render: (_: any, record: any) => (
        <Text>
          {record.currentUsageCount} / {record.maxUsageCount}
        </Text>
      ),
    },
    {
      title: t("couponsPage.columnStatus"),
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "success" : "default"}>
          {isActive ? t("couponsPage.statusActive") : t("couponsPage.statusInactive")}
        </Tag>
      ),
    },
    {
      title: t("couponsPage.columnActions"),
      key: "actions",
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          >
            {t("couponsPage.edit")}
          </Button>
          <Popconfirm
            title={t("couponsPage.deleteConfirm")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("couponsPage.yes")}
            cancelText={t("couponsPage.no")}
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              {t("couponsPage.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const couponType = Form.useWatch("type", form);
  const couponScope = Form.useWatch("scope", form);

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{t("couponsPage.title")}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleOpenModal()}
        >
          {t("couponsPage.addCoupon")}
        </Button>
      </div>

      <Table
        dataSource={coupons || []}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingCoupon ? t("couponsPage.modalEditTitle") : t("couponsPage.modalAddTitle")}
        open={isModalVisible}
        onCancel={handleCloseModal}
        onOk={handleSubmit}
        okText={editingCoupon ? t("couponsPage.save") : t("couponsPage.create")}
        cancelText={t("couponsPage.cancel")}
        width={600}
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="code"
            label={t("couponsPage.codeLabel")}
            rules={[
              { required: true, message: t("couponsPage.codeRequired") },
              {
                pattern: /^[A-Z0-9]+$/,
                message: t("couponsPage.codePattern"),
              },
            ]}
          >
            <Input
              placeholder={t("couponsPage.codePlaceholder")}
              maxLength={20}
              style={{ textTransform: "uppercase" }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label={t("couponsPage.typeLabel")}
            rules={[{ required: true, message: t("couponsPage.typeRequired") }]}
          >
            <Select placeholder={t("couponsPage.typePlaceholder")}>
              <Option value="PERCENTAGE">{t("couponsPage.typeOptionPercentage")}</Option>
              <Option value="FIXED_AMOUNT">{t("couponsPage.typeOptionFixed")}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="value"
            label={
              couponType === "PERCENTAGE" ? t("couponsPage.valueLabelPercentage") : t("couponsPage.valueLabelAmount")
            }
            rules={[
              { required: true, message: t("couponsPage.valueRequired") },
              {
                type: "number",
                min: 0.01,
                message: t("couponsPage.valueMin"),
              },
              ...(couponType === "PERCENTAGE"
                ? [
                  {
                    type: "number" as const,
                    max: 100,
                    message: t("couponsPage.valueMaxPercentage"),
                  },
                ]
                : []),
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0.01}
              max={couponType === "PERCENTAGE" ? 100 : undefined}
              step={couponType === "PERCENTAGE" ? 1 : 0.01}
              placeholder={
                couponType === "PERCENTAGE"
                  ? t("couponsPage.valuePlaceholderPercentage")
                  : t("couponsPage.valuePlaceholderAmount")
              }
            />
          </Form.Item>

          <Form.Item
            name="scope"
            label={t("couponsPage.scopeLabel")}
            rules={[{ required: true, message: t("couponsPage.scopeRequired") }]}
          >
            <Select placeholder={t("couponsPage.scopePlaceholder")}>
              <Option value="CART">{t("couponsPage.scopeOptionCart")}</Option>
              <Option value="SPECIFIC_PRODUCTS">{t("couponsPage.scopeOptionProducts")}</Option>
            </Select>
          </Form.Item>

          {couponScope === "SPECIFIC_PRODUCTS" && (
            <Form.Item
              name="productIds"
              label={t("couponsPage.productsLabel")}
              rules={[
                {
                  required: true,
                  message: t("couponsPage.productsRequired"),
                },
              ]}
            >
              <TreeSelect
                treeData={treeData}
                placeholder={t("couponsPage.productsPlaceholder")}
                multiple
                showSearch
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_CHILD}
                style={{ width: '100%' }}
                treeDefaultExpandAll
                filterTreeNode={(input, node) =>
                  (node.title as string)
                    ?.toLowerCase()
                    ?.indexOf(input.toLowerCase()) >= 0
                }
              />
            </Form.Item>
          )}

          <Form.Item
            name="minimumOrderValue"
            label={t("couponsPage.minOrderLabel")}
            rules={[
              {
                type: "number",
                min: 0,
                message: t("couponsPage.minOrderNegative"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              placeholder={t("couponsPage.minOrderPlaceholder")}
            />
          </Form.Item>

          <Form.Item
            name="maxUsageCount"
            label={t("couponsPage.maxUsageLabel")}
            rules={[
              { required: true, message: t("couponsPage.maxUsageRequired") },
              {
                type: "number",
                min: 1,
                message: t("couponsPage.maxUsageMin"),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              step={1}
              placeholder={t("couponsPage.maxUsagePlaceholder")}
            />
          </Form.Item>

          <SettingItem>
            <SettingRow>
              <Form.Item name="isActive" valuePropName="checked" noStyle>
                <StyledSwitch />
              </Form.Item>
              <SettingContent>
                <SettingLabel>{t("couponsPage.statusSettingLabel")}</SettingLabel>
                <SettingDescription>{t("couponsPage.statusSettingDescription")}</SettingDescription>
              </SettingContent>
            </SettingRow>
          </SettingItem>
        </Form>
      </Modal>
    </div>
  );
};
